import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';
import { useAuth } from './auth';
import type { MacPermission } from './mac';
import { projectKeys } from './projects';
import { searchKeys } from './search';
import type { Model } from './settings';

export type SessionRow = {
  id: string;
  user_id: number;
  title: string;
  claude_session_id: string | null;
  model: string | null;
  created_at: number;
  last_msg_at: number;
  project_id: string | null;
  starred: number;
  /**
   * The CLI-reported `usage` block from the most recent `result`
   * event of this session, serialised as JSON. The ChatHeader meter
   * reads it as a fallback when no live run is streaming so the
   * context-window indicator survives reload, day breaks, etc.
   */
  last_usage_json: string | null;
  last_usage_at: number | null;
  /** 1 = «Мак» chat (admin only): the model also acts on the owner's MacBook. */
  mac_mode?: number;
  mac_permission?: MacPermission;
};

export type SessionPatch = {
  title?: string;
  starred?: boolean;
  project_id?: string | null;
  mac_permission?: MacPermission;
};

export type MessageRole = 'user' | 'assistant' | 'system';

export type MessageRow = {
  id: string;
  session_id: string;
  role: MessageRole;
  content: string;
  created_at: number;
  tool_calls_json: string | null;
  /** JSON-encoded ordered list of content blocks for assistant
   * turns. Added in DB migration 12. `null` for user/system rows
   * and for assistant rows persisted before the migration ran —
   * the client falls back to "text first, tools after" for those. */
  content_blocks_json: string | null;
  /** Raw extended-thinking trace for the assistant turn (DB
   * migration 13). `null` for user/system rows, pre-migration
   * assistant rows, and turns where the model didn't reason
   * (--effort minimal / non-reasoning model). When present, the
   * client renders it through `ThinkingBlock` so the user can
   * read the reasoning long after the streaming finished. */
  thinking_text: string | null;
  /** Normalized uploads-relative paths for user uploads. Legacy rows are null. */
  attachments_json?: string | null;
};

export const sessionKeys = {
  all: ['sessions'] as const,
  list: (userId: number) => ['sessions', userId] as const,
  messages: (id: string) => ['messages', id] as const,
  messageList: (id: string, userId: number) => ['messages', id, userId] as const,
};

export function useSessions() {
  const { user } = useAuth();
  return useQuery({
    queryKey: user ? sessionKeys.list(user.id) : ['sessions', 'nil'],
    enabled: user != null,
    queryFn: async () => {
      const r = await api<{ sessions: SessionRow[] }>('/sessions');
      return r.sessions;
    },
  });
}

export function useMessages(sessionId: string | null | undefined) {
  const { user } = useAuth();
  return useQuery({
    queryKey: sessionId && user ? sessionKeys.messageList(sessionId, user.id) : ['messages', 'nil'],
    enabled: !!sessionId && user != null,
    // A run can finish while this page is unmounted or the tab sleeps.
    staleTime: 0,
    refetchOnWindowFocus: true,
    queryFn: async ({ signal }) => {
      const r = await api<{ messages: MessageRow[] }>(`/sessions/${sessionId}/messages`, {
        signal,
      });
      return r.messages;
    },
  });
}

export function useCreateSession() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (
      body: {
        title?: string;
        model?: Model;
        project_id?: string;
        mac_mode?: boolean;
        mac_permission?: MacPermission;
      } = {},
    ) => {
      const r = await api<{ session: SessionRow }>('/sessions', {
        method: 'POST',
        body,
      });
      return r.session;
    },
    onSuccess: (_session, body) => {
      if (!user) return;
      void qc.invalidateQueries({ queryKey: sessionKeys.list(user.id), exact: true });
      if (body.project_id) void qc.invalidateQueries({ queryKey: projectKeys.user(user.id) });
    },
  });
}

export function useDeleteSession() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (id: string) => {
      await api(`/sessions/${id}`, { method: 'DELETE' });
    },
    // Drop the row from the sidebar the instant the user confirms.
    // If the request fails we put it back before surfacing the error.
    onMutate: async (id) => {
      if (!user) return { prev: undefined };
      const key = sessionKeys.list(user.id);
      await qc.cancelQueries({ queryKey: key, exact: true });
      const prev = qc.getQueryData<SessionRow[]>(key);
      if (prev) {
        qc.setQueryData<SessionRow[]>(
          key,
          prev.filter((s) => s.id !== id),
        );
      }
      return { prev };
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.prev && user) qc.setQueryData(sessionKeys.list(user.id), ctx.prev);
    },
    onSuccess: (_data, id) => {
      if (!user) return;
      qc.removeQueries({ queryKey: sessionKeys.messageList(id, user.id), exact: true });
      void qc.invalidateQueries({ queryKey: projectKeys.user(user.id) });
      void qc.invalidateQueries({ queryKey: searchKeys.user(user.id) });
    },
    onSettled: () => {
      if (user) void qc.invalidateQueries({ queryKey: sessionKeys.list(user.id), exact: true });
    },
  });
}

/**
 * Patch a session — title, starred, or project_id. Optimistically
 * updates the sidebar list so Star flashes immediately and Rename
 * shows the new title the moment the user presses Enter.
 */
export function usePatchSession() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: SessionPatch }) => {
      const r = await api<{ session: SessionRow }>(`/sessions/${id}`, {
        method: 'PATCH',
        body: patch,
      });
      return r.session;
    },
    onMutate: async ({ id, patch }) => {
      if (!user) return { prev: undefined };
      const key = sessionKeys.list(user.id);
      await qc.cancelQueries({ queryKey: key, exact: true });
      const prev = qc.getQueryData<SessionRow[]>(key);
      if (prev) {
        qc.setQueryData<SessionRow[]>(
          key,
          prev.map((s) =>
            s.id === id
              ? {
                  ...s,
                  ...(patch.title !== undefined ? { title: patch.title } : {}),
                  ...(patch.starred !== undefined ? { starred: patch.starred ? 1 : 0 } : {}),
                  ...(patch.project_id !== undefined ? { project_id: patch.project_id } : {}),
                  ...(patch.mac_permission !== undefined
                    ? { mac_permission: patch.mac_permission }
                    : {}),
                }
              : s,
          ),
        );
      }
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev && user) qc.setQueryData(sessionKeys.list(user.id), ctx.prev);
    },
    onSuccess: (_session, { patch }) => {
      if (!user) return;
      void qc.invalidateQueries({ queryKey: projectKeys.user(user.id) });
      if (patch.title !== undefined)
        void qc.invalidateQueries({ queryKey: searchKeys.user(user.id) });
    },
    onSettled: () => {
      if (user) void qc.invalidateQueries({ queryKey: sessionKeys.list(user.id), exact: true });
    },
  });
}
